<script setup>
  import { reactive, watch, onMounted } from 'vue';
  import VariantSelector from '@/apps/tickets/components/VariantSelector.vue';

  const props = defineProps({
    tickets: {
      type: Array,
      default: () => [],
    },
    event: {
      type: Object,
      default: () => ({}),
    },
    reservedTickets: {
      type: Array,
      default: () => [],
    },
    viewMode: {
      type: String,
      default: 'grid',
      validator: (value) => ['grid', 'list'].includes(value),
    },
  });

  const emit = defineEmits([
    'add-tickets',
    'remove-ticket',
    'back-to-overview',
  ]);

  // For non-variant products: keyed by ticketId
  // For variant products: keyed by "ticketId:variantLabel"
  const cartItems = reactive({});
  const selectedVariants = reactive({});
  const variantQuantities = reactive({});

  function hasVariants(ticket) {
    return ticket.metadata?.variant_config?.enabled;
  }

  function getCartKey(ticketId, variantLabel = null) {
    return variantLabel ? `${ticketId}:${variantLabel}` : `${ticketId}`;
  }

  function loadExistingCartItems() {
    Object.keys(cartItems).forEach((key) => delete cartItems[key]);

    props.reservedTickets.forEach((ticket) => {
      // Check if this reserved ticket has variant info
      const variantLabel = ticket.selectedVariant?.label || null;
      const key = getCartKey(ticket.type, variantLabel);
      cartItems[key] = (cartItems[key] || 0) + 1;
    });
  }

  watch(
    () => props.reservedTickets,
    () => loadExistingCartItems(),
    { deep: true }
  );

  onMounted(() => loadExistingCartItems());

  function canAddToCart(ticketId) {
    const ticket = props.tickets.find((t) => t.id === ticketId);
    if (!ticket) return false;

    if (hasVariants(ticket)) {
      const selectedIndex = selectedVariants[ticket.id];
      return selectedIndex !== undefined && selectedIndex !== null;
    }

    return true;
  }

  function getTicketPrice(ticket) {
    let price = ticket.price || 0;

    if (hasVariants(ticket)) {
      const selectedIndex = selectedVariants[ticket.id];
      if (selectedIndex !== undefined && selectedIndex !== null) {
        const variant = ticket.metadata.variant_config.variants[selectedIndex];
        if (variant && variant.price_modifier) {
          price += parseFloat(variant.price_modifier);
        }
      }
    }

    return price;
  }

  function getVariantQuantity(ticketId) {
    return variantQuantities[ticketId] || 1;
  }

  function setVariantQuantity(ticketId, value) {
    const num = parseInt(value) || 1;
    variantQuantities[ticketId] = Math.max(1, num);
  }

  function addToCart(ticket) {
    if (hasVariants(ticket)) {
      addVariantToCart(ticket);
    } else {
      addSimpleToCart(ticket);
    }
  }

  function addSimpleToCart(ticket) {
    emit('add-tickets', {
      ticketId: ticket.id,
      eventId: props.event.id,
      num: 1,
      selectedVariant: null,
    });

    const key = getCartKey(ticket.id);
    cartItems[key] = (cartItems[key] || 0) + 1;
  }

  function addVariantToCart(ticket) {
    const selectedIndex = selectedVariants[ticket.id];
    if (selectedIndex === undefined || selectedIndex === null) return;

    const variant = ticket.metadata.variant_config.variants[selectedIndex];
    if (!variant) return;

    const quantity = getVariantQuantity(ticket.id);

    emit('add-tickets', {
      ticketId: ticket.id,
      eventId: props.event.id,
      num: quantity,
      selectedVariant: variant,
    });

    const key = getCartKey(ticket.id, variant.label);
    cartItems[key] = (cartItems[key] || 0) + quantity;

    // Reset quantity input after adding
    variantQuantities[ticket.id] = 1;
  }

  function removeFromCart(ticket) {
    const key = getCartKey(ticket.id);
    if (cartItems[key] > 0) {
      cartItems[key]--;
      if (cartItems[key] === 0) {
        delete cartItems[key];
      }

      emit('remove-ticket', { ticketType: ticket.id, selectedVariant: null });
    }
  }

  function getCartQuantity(ticketId) {
    // For non-variant products, just return the simple key
    const simpleKey = getCartKey(ticketId);
    if (cartItems[simpleKey]) return cartItems[simpleKey];

    // For variant products, sum all variant quantities
    let total = 0;
    const prefix = `${ticketId}:`;
    Object.keys(cartItems).forEach((key) => {
      if (key.startsWith(prefix)) {
        total += cartItems[key];
      }
    });
    return total;
  }

  function getVariantCartLines(ticketId) {
    const lines = [];
    const prefix = `${ticketId}:`;
    Object.keys(cartItems).forEach((key) => {
      if (key.startsWith(prefix) && cartItems[key] > 0) {
        const variantLabel = key.substring(prefix.length);
        lines.push({ label: variantLabel, quantity: cartItems[key] });
      }
    });
    return lines;
  }

  function removeVariantFromCart(ticket, variantLabel) {
    const key = getCartKey(ticket.id, variantLabel);
    if (cartItems[key] > 0) {
      cartItems[key]--;
      if (cartItems[key] === 0) {
        delete cartItems[key];
      }

      emit('remove-ticket', {
        ticketType: ticket.id,
        selectedVariant: { label: variantLabel },
      });
    }
  }

  function formatPrice(price) {
    if (price === null || price === undefined) return '€ 0,00';
    return `€ ${parseFloat(price).toFixed(2).replace('.', ',')}`;
  }
</script>

<template>
  <div class="product-grid">
    <h3 class="mb-4 text-lg font-medium text-gray-900">
      {{ $t('products.title') }}
    </h3>

    <!-- === GRID VIEW === -->
    <div
      v-if="viewMode === 'grid'"
      class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    >
      <div
        v-for="ticket in tickets"
        :key="ticket.id"
        class="group relative flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md"
      >
        <!-- Product Image -->
        <div class="aspect-square w-full overflow-hidden bg-gray-100">
          <img
            v-if="ticket.imageUrl"
            :src="ticket.imageUrl"
            :alt="ticket.title || ticket.name"
            class="h-full w-full object-cover object-center transition-transform duration-200 group-hover:scale-105"
          />
          <div
            v-else
            class="flex h-full w-full items-center justify-center bg-gray-100"
          >
            <i class="fa-solid fa-box text-4xl text-gray-400"></i>
          </div>
        </div>

        <!-- Product Info -->
        <div class="flex flex-1 flex-col p-4">
          <h4 class="mb-2 text-sm font-medium text-gray-900">
            {{ ticket.title || ticket.name }}
          </h4>

          <!-- Price -->
          <div class="mb-3">
            <span class="text-primary-600 text-lg font-bold">
              {{ formatPrice(getTicketPrice(ticket)) }}
            </span>
            <span
              v-if="
                ticket.metadata?.variant_config?.enabled &&
                selectedVariants[ticket.id] !== undefined &&
                ticket.metadata.variant_config.variants[
                  selectedVariants[ticket.id]
                ]?.price_modifier
              "
              class="ml-1 text-xs text-gray-500"
            >
              (basis: {{ formatPrice(ticket.price) }})
            </span>
          </div>

          <!-- === VARIANT PRODUCT CONTROLS === -->
          <template v-if="hasVariants(ticket)">
            <!-- Variant Selector -->
            <div class="mb-3">
              <VariantSelector
                v-model="selectedVariants[ticket.id]"
                :config="ticket.metadata.variant_config"
              />
            </div>

            <!-- Quantity + Add button -->
            <div class="mt-auto">
              <div class="flex items-center gap-2">
                <div
                  class="flex items-center overflow-hidden rounded-md border border-gray-300"
                >
                  <button
                    type="button"
                    class="flex h-[34px] w-9 items-center justify-center bg-white text-gray-600 transition-colors hover:bg-gray-50"
                    @click="
                      setVariantQuantity(
                        ticket.id,
                        getVariantQuantity(ticket.id) - 1
                      )
                    "
                  >
                    <i class="fa-solid fa-minus text-xs"></i>
                  </button>
                  <input
                    type="number"
                    :value="getVariantQuantity(ticket.id)"
                    min="1"
                    class="h-[34px] w-10 !border-y-0 border-x border-gray-300 bg-white text-center text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    @input="setVariantQuantity(ticket.id, $event.target.value)"
                  />
                  <button
                    type="button"
                    class="flex h-[34px] w-9 items-center justify-center bg-white text-gray-600 transition-colors hover:bg-gray-50"
                    @click="
                      setVariantQuantity(
                        ticket.id,
                        getVariantQuantity(ticket.id) + 1
                      )
                    "
                  >
                    <i class="fa-solid fa-plus text-xs"></i>
                  </button>
                </div>
                <button
                  type="button"
                  class="inline-flex h-[36px] flex-1 items-center justify-center rounded-md px-3 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                  :class="
                    canAddToCart(ticket.id)
                      ? 'bg-primary-600 hover:bg-primary-700'
                      : 'bg-gray-400'
                  "
                  :disabled="!canAddToCart(ticket.id)"
                  @click="addToCart(ticket)"
                >
                  <i class="fa-solid fa-plus mr-1.5 text-xs"></i>
                  Toevoegen
                </button>
              </div>

              <!-- Cart lines per variant -->
              <div
                v-if="getVariantCartLines(ticket.id).length > 0"
                class="mt-3 space-y-1"
              >
                <div
                  v-for="line in getVariantCartLines(ticket.id)"
                  :key="line.label"
                  class="flex items-center justify-between rounded bg-gray-50 px-2 py-1 text-xs"
                >
                  <span class="font-medium text-gray-700">
                    {{ line.quantity }}x {{ line.label }}
                  </span>
                  <button
                    type="button"
                    class="text-red-500 transition-colors hover:text-red-700"
                    @click="removeVariantFromCart(ticket, line.label)"
                  >
                    <i class="fa-solid fa-minus"></i>
                  </button>
                </div>
              </div>
            </div>
          </template>

          <!-- === NON-VARIANT PRODUCT CONTROLS (simple +/- stepper) === -->
          <div v-else class="mt-auto">
            <div v-if="getCartQuantity(ticket.id) === 0">
              <button
                type="button"
                class="bg-primary-600 hover:bg-primary-700 inline-flex w-full items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium text-white transition-colors"
                @click="addToCart(ticket)"
              >
                <i class="fa-solid fa-plus mr-1.5 text-xs"></i>
                Toevoegen
              </button>
            </div>
            <div v-else class="flex items-center justify-between">
              <div
                class="flex items-center overflow-hidden rounded-md border border-gray-300"
              >
                <button
                  type="button"
                  class="flex h-[34px] w-9 items-center justify-center bg-white text-gray-600 transition-colors hover:bg-gray-50"
                  @click="removeFromCart(ticket)"
                >
                  <i class="fa-solid fa-minus text-xs"></i>
                </button>
                <div
                  class="flex h-[34px] min-w-[2rem] items-center justify-center border-x border-gray-300 bg-white px-3 text-sm font-medium"
                >
                  {{ getCartQuantity(ticket.id) }}
                </div>
                <button
                  type="button"
                  class="flex h-[34px] w-9 items-center justify-center bg-white text-gray-600 transition-colors hover:bg-gray-50"
                  @click="addToCart(ticket)"
                >
                  <i class="fa-solid fa-plus text-xs"></i>
                </button>
              </div>
              <div class="text-primary-600 text-xs font-medium">
                <i class="fa-solid fa-shopping-cart mr-1"></i>
                In winkelwagen
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- === LIST VIEW === -->
    <div v-else class="space-y-3">
      <div
        v-for="ticket in tickets"
        :key="ticket.id"
        class="rounded-lg border border-gray-200 bg-white p-4 transition-shadow hover:shadow-sm"
      >
        <div class="flex items-start gap-4">
          <!-- Product Image (small thumbnail) -->
          <div
            v-if="ticket.imageUrl"
            class="h-14 w-14 flex-shrink-0 overflow-hidden rounded-md bg-gray-100"
          >
            <img
              :src="ticket.imageUrl"
              :alt="ticket.title || ticket.name"
              class="h-full w-full object-cover object-center"
            />
          </div>
          <div
            v-else
            class="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-md bg-gray-100"
          >
            <i class="fa-solid fa-box text-lg text-gray-400"></i>
          </div>

          <!-- Product Info + Controls -->
          <div class="min-w-0 flex-1">
            <div class="flex items-center justify-between gap-4">
              <!-- Name + Price -->
              <div class="min-w-0">
                <h4 class="text-sm font-medium text-gray-900">
                  {{ ticket.title || ticket.name }}
                </h4>
                <span class="text-primary-600 text-sm font-bold">
                  {{ formatPrice(getTicketPrice(ticket)) }}
                </span>
                <span
                  v-if="
                    ticket.metadata?.variant_config?.enabled &&
                    selectedVariants[ticket.id] !== undefined &&
                    ticket.metadata.variant_config.variants[
                      selectedVariants[ticket.id]
                    ]?.price_modifier
                  "
                  class="ml-1 text-xs text-gray-500"
                >
                  (basis: {{ formatPrice(ticket.price) }})
                </span>
              </div>

              <!-- Controls (right side) -->
              <div class="flex flex-shrink-0 items-center gap-2">
                <!-- Variant controls -->
                <template v-if="hasVariants(ticket)">
                  <div class="w-44">
                    <VariantSelector
                      v-model="selectedVariants[ticket.id]"
                      :config="ticket.metadata.variant_config"
                      hideLabel
                    />
                  </div>
                  <div
                    class="flex items-center overflow-hidden rounded-md border border-gray-300"
                  >
                    <button
                      type="button"
                      class="flex h-[34px] w-9 items-center justify-center bg-white text-gray-600 transition-colors hover:bg-gray-50"
                      @click="
                        setVariantQuantity(
                          ticket.id,
                          getVariantQuantity(ticket.id) - 1
                        )
                      "
                    >
                      <i class="fa-solid fa-minus text-xs"></i>
                    </button>
                    <input
                      type="number"
                      :value="getVariantQuantity(ticket.id)"
                      min="1"
                      class="h-[34px] w-10 !border-y-0 border-x border-gray-300 bg-white text-center text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      @input="
                        setVariantQuantity(ticket.id, $event.target.value)
                      "
                    />
                    <button
                      type="button"
                      class="flex h-[34px] w-9 items-center justify-center bg-white text-gray-600 transition-colors hover:bg-gray-50"
                      @click="
                        setVariantQuantity(
                          ticket.id,
                          getVariantQuantity(ticket.id) + 1
                        )
                      "
                    >
                      <i class="fa-solid fa-plus text-xs"></i>
                    </button>
                  </div>
                  <button
                    type="button"
                    class="inline-flex h-[36px] items-center justify-center rounded-md px-3 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                    :class="
                      canAddToCart(ticket.id)
                        ? 'bg-primary-600 hover:bg-primary-700'
                        : 'bg-gray-400'
                    "
                    :disabled="!canAddToCart(ticket.id)"
                    @click="addToCart(ticket)"
                  >
                    <i class="fa-solid fa-plus mr-1.5 text-xs"></i>
                    Toevoegen
                  </button>
                </template>

                <!-- Non-variant controls -->
                <template v-else>
                  <div v-if="getCartQuantity(ticket.id) === 0">
                    <button
                      type="button"
                      class="bg-primary-600 hover:bg-primary-700 inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium text-white transition-colors"
                      @click="addToCart(ticket)"
                    >
                      <i class="fa-solid fa-plus mr-1.5 text-xs"></i>
                      Toevoegen
                    </button>
                  </div>
                  <template v-else>
                    <div
                      class="flex items-center overflow-hidden rounded-md border border-gray-300"
                    >
                      <button
                        type="button"
                        class="flex h-[34px] w-9 items-center justify-center bg-white text-gray-600 transition-colors hover:bg-gray-50"
                        @click="removeFromCart(ticket)"
                      >
                        <i class="fa-solid fa-minus text-xs"></i>
                      </button>
                      <div
                        class="flex h-[34px] min-w-[2rem] items-center justify-center border-x border-gray-300 bg-white px-3 text-sm font-medium"
                      >
                        {{ getCartQuantity(ticket.id) }}
                      </div>
                      <button
                        type="button"
                        class="flex h-[34px] w-9 items-center justify-center bg-white text-gray-600 transition-colors hover:bg-gray-50"
                        @click="addToCart(ticket)"
                      >
                        <i class="fa-solid fa-plus text-xs"></i>
                      </button>
                    </div>
                    <div
                      class="text-primary-600 whitespace-nowrap text-xs font-medium"
                    >
                      <i class="fa-solid fa-shopping-cart mr-1"></i>
                      In winkelwagen
                    </div>
                  </template>
                </template>
              </div>
            </div>

            <!-- Cart lines per variant (below the row) -->
            <div
              v-if="
                hasVariants(ticket) && getVariantCartLines(ticket.id).length > 0
              "
              class="mt-2 space-y-1"
            >
              <div
                v-for="line in getVariantCartLines(ticket.id)"
                :key="line.label"
                class="flex items-center justify-between rounded bg-gray-50 px-3 py-1 text-xs"
              >
                <span class="font-medium text-gray-700">
                  {{ line.quantity }}x {{ line.label }}
                </span>
                <button
                  type="button"
                  class="text-red-500 transition-colors hover:text-red-700"
                  @click="removeVariantFromCart(ticket, line.label)"
                >
                  <i class="fa-solid fa-minus"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="tickets.length === 0" class="py-12 text-center">
      <i class="fa-solid fa-box-open mb-4 text-4xl text-gray-400"></i>
      <h3 class="mb-2 text-lg font-medium text-gray-900">
        {{ $t('products.empty.title') }}
      </h3>
      <p class="text-gray-500">
        {{ $t('products.empty.description') }}
      </p>
    </div>
  </div>
</template>

<style>
  .product-grid {
    @apply w-full;
  }
</style>
